import type { ISurveyTranslationRow } from '@/features/survey/survey-translation-section'
import type { IPoiCategory } from '@/shared/api/types/poi-types'
import { useNavigate, useParams } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath } from 'react-router'
import {
  usePoiCategoriesQuery,
  usePoiCategoryByIdQuery,
  useUpdatePoiCategoryMutation,
} from '@/entities/poi'
import { useTenantByIdQuery, useTenantLanguages } from '@/entities/tenant'
import { PoiCategoryForm } from '@/features/poi'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { useScopedAdminPath } from '@/shared/hooks/use-scoped-admin-path'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { PageContainer } from '@/shared/ui/page-container'
import { PageHeader } from '@/shared/ui/page-header'
import { hasRequiredTranslations, resolveDefaultLanguages } from '@/shared/utils/language-utils'
import { resolvePoiCategoryName } from '@/shared/utils/poi-category-utils'

const DEFAULT_POI_CATEGORY_COLOR = '#000000'

function mergeCategoryTranslationRows(
  languages: string[],
  category?: IPoiCategory | null,
): ISurveyTranslationRow[] {
  return languages.map((language) => ({
    language,
    name:
      category?.localizedData?.[language]?.name ?? category?.localizedData?.[language]?.code ?? '',
    description: '',
  }))
}

function buildLocalizedDataPayload(rows: ISurveyTranslationRow[]) {
  return rows.reduce<Record<string, { name: string }>>((acc, row) => {
    const name = row.name.trim()
    if (name) {
      acc[row.language] = { name }
    }
    return acc
  }, {})
}

export function PoiCategoryEditPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const params = useParams({ strict: false })
  const scopedAdminPath = useScopedAdminPath()
  const { activeTenantId = '', setActiveTenantId } = useAdminScope()
  const tenantId = params.tenantId ?? params.id ?? ''
  const categoryId = params.categoryId ?? ''
  const listPath = scopedAdminPath(generatePath('/tenant/:tenantId/poi-categories', { tenantId }))

  const { data: tenantResponse, isLoading: tenantLoading } = useTenantByIdQuery(tenantId)
  const tenantLanguagesQuery = useTenantLanguages(tenantId)
  const categoryQuery = usePoiCategoryByIdQuery(categoryId)
  const categoriesQuery = usePoiCategoriesQuery(tenantId, {})
  const updateMutation = useUpdatePoiCategoryMutation()

  const languageCodes = useMemo(
    () => resolveDefaultLanguages(tenantLanguagesQuery.items),
    [tenantLanguagesQuery.items],
  )
  const category = categoryQuery.data?.data ?? null
  const [translations, setTranslations] = useState<ISurveyTranslationRow[]>([])
  const [icon, setIcon] = useState('')
  const [iconActive, setIconActive] = useState('')
  const [color, setColor] = useState(DEFAULT_POI_CATEGORY_COLOR)
  const [isIconUploading, setIsIconUploading] = useState(false)
  const [isIconActiveUploading, setIsIconActiveUploading] = useState(false)
  const [savingSection, setSavingSection] = useState<'icon' | 'color' | 'localized' | null>(null)
  const iconInitRef = useRef('')
  const translationInitRef = useRef('')

  useEffect(() => {
    if (!tenantId || activeTenantId === tenantId) {
      return
    }
    setActiveTenantId(tenantId)
  }, [activeTenantId, setActiveTenantId, tenantId])

  useEffect(() => {
    if (!category) {
      return
    }
    const signature = `${category.id}:${category.icon ?? ''}:${category.iconActive ?? ''}:${category.color ?? ''}`
    if (iconInitRef.current === signature) {
      return
    }
    iconInitRef.current = signature
    setIcon(category.icon ?? '')
    setIconActive(category.iconActive ?? '')
    setColor(category.color ?? DEFAULT_POI_CATEGORY_COLOR)
    setIsIconUploading(false)
  }, [category])

  useEffect(() => {
    if (!category || languageCodes.length < 1) {
      return
    }
    const signature = `${category.id}:${languageCodes.join('\0')}:${JSON.stringify(category.localizedData ?? {})}`
    if (translationInitRef.current === signature) {
      return
    }
    translationInitRef.current = signature
    setTranslations(mergeCategoryTranslationRows(languageCodes, category))
  }, [category, languageCodes])

  const canValidateDuplicateCategories = categoriesQuery.isSuccess
  const isIconSubmitDisabled =
    updateMutation.isPending ||
    isIconUploading ||
    isIconActiveUploading ||
    categoryQuery.isLoading ||
    !categoryId
  const isLocalizedSubmitDisabled =
    updateMutation.isPending ||
    categoryQuery.isLoading ||
    languageCodes.length < 1 ||
    !canValidateDuplicateCategories
  const isColorSubmitDisabled = updateMutation.isPending || categoryQuery.isLoading || !categoryId

  async function handleIconSubmit() {
    if (isIconUploading || isIconActiveUploading) {
      showToast.error(
        t('poi.media.waitForUpload', {
          defaultValue: 'Please wait for image upload to finish before saving.',
        }),
      )
      return
    }

    try {
      setSavingSection('icon')
      await updateMutation.mutateAsync({
        id: categoryId,
        payload: {
          icon: icon.trim(),
          iconActive: iconActive.trim(),
        },
      })
      showToast.success(t('poiCategories.updateIconSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setSavingSection(null)
    }
  }

  async function handleLocalizedSubmit() {
    if (!hasRequiredTranslations(translations, ['name'], languageCodes)) {
      showToast.error(t('poiCategories.allLanguagesRequired'))
      return
    }
    if (!canValidateDuplicateCategories) {
      showToast.error(
        t('poiCategories.loadFailed', {
          defaultValue: 'Could not load categories to validate duplicates. Please try again.',
        }),
      )
      return
    }

    const localizedData = buildLocalizedDataPayload(translations)
    const duplicate = categoriesQuery.data.data?.some(
      (item) =>
        item.id !== categoryId &&
        Object.entries(localizedData).some(([language, value]) => {
          const existing = (item.localizedData?.[language]?.name ?? '').trim()
          return existing.toLowerCase() === value.name.toLowerCase()
        }),
    )
    if (duplicate) {
      showToast.error(
        t('poiCategories.nameDuplicate', { defaultValue: 'Category name already exists' }),
      )
      return
    }

    try {
      setSavingSection('localized')
      await updateMutation.mutateAsync({
        id: categoryId,
        payload: {
          localizedData,
        },
      })
      showToast.success(t('poiCategories.updateLocalizedSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setSavingSection(null)
    }
  }

  async function handleColorSubmit() {
    try {
      setSavingSection('color')
      await updateMutation.mutateAsync({
        id: categoryId,
        payload: { color },
      })
      showToast.success(t('poiCategories.updateColorSuccess'))
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    } finally {
      setSavingSection(null)
    }
  }

  const tenantName = tenantResponse?.data?.name ?? tenantId
  const categoryName = resolvePoiCategoryName(category, languageCodes)

  return (
    <PageContainer>
      <PageHeader
        title={t('poiCategories.edit')}
        breadcrumbs={[
          { label: t('sidebar.tenants'), path: scopedAdminPath('/tenant') },
          ...(tenantId
            ? [
                {
                  label: tenantLoading ? t('common.loading') : tenantName || tenantId,
                  path: scopedAdminPath(generatePath('/tenant/:tenantId', { tenantId })),
                },
              ]
            : []),
          { label: t('poiCategories.title'), path: listPath },
          { label: categoryName || t('poiCategories.edit') },
        ]}
        actions={
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void navigate({ to: listPath as never })
            }}
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </Button>
        }
      />

      <PoiCategoryForm
        icon={icon}
        iconActive={iconActive}
        color={color}
        translations={translations}
        languageCodes={languageCodes}
        translationEntityId={categoryId}
        entityLabel={categoryName || t('poiCategories.edit')}
        isSubmitting={updateMutation.isPending || categoryQuery.isLoading}
        isSubmitDisabled={isIconSubmitDisabled || isLocalizedSubmitDisabled}
        isIconSubmitDisabled={isIconSubmitDisabled}
        isColorSubmitDisabled={isColorSubmitDisabled}
        isLocalizedSubmitDisabled={isLocalizedSubmitDisabled}
        isIconSubmitting={savingSection === 'icon'}
        isColorSubmitting={savingSection === 'color'}
        isLocalizedSubmitting={savingSection === 'localized'}
        iconSubmitLabel={t('poiCategories.updateIcon')}
        colorSubmitLabel={t('poiCategories.updateColor')}
        localizedSubmitLabel={t('poiCategories.updateLocalized')}
        onIconChange={setIcon}
        onColorChange={setColor}
        onIconUploadingChange={setIsIconUploading}
        onIconActiveChange={setIconActive}
        onIconActiveUploadingChange={setIsIconActiveUploading}
        onTranslationsChange={setTranslations}
        onSubmit={handleLocalizedSubmit}
        onIconSubmit={handleIconSubmit}
        onColorSubmit={handleColorSubmit}
        onLocalizedSubmit={handleLocalizedSubmit}
      />
    </PageContainer>
  )
}
